import { useState } from 'react'
import { PartyPopper } from 'lucide-react'
import { getOrCreateSessionId } from '@/utils/sessionId'
import { joinWaitlist } from '@/services/validationApi'
import type { Slide } from './slideData'

interface WaitlistFormProps {
  slide: Slide | null
  onDone: () => void
}

export default function WaitlistForm({ slide, onDone }: WaitlistFormProps) {
  const [form, setForm] = useState({
    name: '',
    email: '',
    firmName: '',
    firmSize: '',
    phone: '',
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  function handleChange(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) {
    setForm({ ...form, [e.target.name]: e.target.value })
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setLoading(true)
    setError('')
    try {
      const sessionId = getOrCreateSessionId()
      const res = await joinWaitlist({ sessionId, ...form })
      if (!res.ok) throw new Error('Something went wrong')
      onDone()
    } catch {
      setError('Could not save your details. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="w-full max-w-md mx-auto px-4">
      <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8">
        {/* Header */}
        <div className="mb-6 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-green-50">
            <PartyPopper className="h-7 w-7 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900">You're one of us.</h2>
          <p className="mt-2 text-sm leading-relaxed text-gray-500">
            We're building TaxHive for practices like yours. Join the waitlist and
            we'll reach out personally before anyone else gets access.
          </p>
          {slide && (
            <p className="mt-3 text-xs text-gray-400">
              You identified: <strong className="text-gray-600">"{slide.title}"</strong>
            </p>
          )}
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Your Name *
            </label>
            <input
              name="name"
              value={form.name}
              onChange={handleChange}
              required
              placeholder="CA Rahul Mehta"
              className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Email *
            </label>
            <input
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              required
              placeholder="rahul@mfirm.com"
              className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Firm Name
            </label>
            <input
              name="firmName"
              value={form.firmName}
              onChange={handleChange}
              placeholder="Mehta & Associates"
              className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Team Size
            </label>
            <select
              name="firmSize"
              value={form.firmSize}
              onChange={handleChange}
              className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select...</option>
              <option value="solo">Solo Practice</option>
              <option value="2-5">2–5 people</option>
              <option value="6-15">6–15 people</option>
              <option value="16-50">16–50 people</option>
              <option value="50+">50+ people</option>
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Phone (optional)
            </label>
            <input
              name="phone"
              value={form.phone}
              onChange={handleChange}
              placeholder="+91 98765 43210"
              className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {error && (
            <p className="text-sm text-red-600">{error}</p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-lg bg-blue-600 py-3 text-sm font-semibold text-white transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
          >
            {loading ? 'Saving...' : 'Reserve My Spot \u2192'}
          </button>
        </form>
      </div>
    </div>
  )
}
